/**
 * HOW TO USE
 *
 * use create-content-loader to generate the desired skeleton svg:
 * https://danilowoz.com/create-content-loader/
 *
 * How it works:
 * 1) get dimensions of component to skeleton and put into create-content-loader
 * 2) use best guess estimates to mock out placements for all elements you
 *  want to show in the skeleton
 * 3) transfer this to code
 * 4) load page and check the position/size for each element
 * Adjust position in code as necessary
 * 5) rinse and repeat until you hit your desired level of pixel perfection
 */

import React, { ReactNode } from 'react';
import ContentLoader from 'react-content-loader';
import { useTheme } from 'react-jss';
import type { Theme } from '@wds/styles';

import { WebAssetsThemeWrapper } from '../../theme';
// https://github.com/danilowoz/react-content-loader/issues/194
import InitialComponent, { InitialComponentProps } from './InitialComponent';

export const DEFAULT_COLOR = '#6B787A' as const;

export interface SkeletonLoaderProps {
  backgroundColor?: string;
  backgroundOpacity?: number;
  foregroundColor?: string;
  foregroundOpacity?: number;
  speed?: number;
  children: ReactNode;
  [other: string]: any;
}

const SkeletonLoaderContent = (
  props: SkeletonLoaderProps & InitialComponentProps
) => {
  const theme: Theme = useTheme();
  const { backgroundColor, foregroundColor, children, ...other } = props;

  const bgColor =
    backgroundColor || theme?.palette?.secondary?.main || DEFAULT_COLOR;
  const fgColor =
    foregroundColor || theme?.palette?.secondary?.main || DEFAULT_COLOR;

  return (
    <ContentLoader
      backgroundColor={bgColor}
      foregroundColor={fgColor}
      {...other}
    >
      {children || <InitialComponent {...props} />}
    </ContentLoader>
  );
};

const SkeletonLoader = (props: SkeletonLoaderProps & InitialComponentProps) => (
  <WebAssetsThemeWrapper>
    <SkeletonLoaderContent {...props} />
  </WebAssetsThemeWrapper>
);

SkeletonLoader.defaultProps = {
  children: null,
  // background
  backgroundColor: '',
  backgroundOpacity: 0.08,
  // animation
  foregroundColor: '',
  foregroundOpacity: 0.18,
  // speed
  speed: 2,
} as Partial<SkeletonLoaderProps>;

export default SkeletonLoader;
